import {
  EventTypes,
  HISTORY_ACTION_MODE,
  NodeId,
  nodesFlux,
  ROOT_NODE,
  useEditorSelector,
} from '@candulabs/craft';
import { ArrowRightIcon, Caption, css, styled } from 'canoe';
import React from 'react';

import { BuiltInComponents, selectionBlacklist } from 'craft/constants';
import { useEditor, useSelection } from 'craft/hooks';
import { useTraversal } from 'craft/hooks/useSelection/useTraversal';
import { useComponentSchema, useFormAncestor } from 'craft/state';

const BreadcrumbName = styled(Caption)<{
  isLast?: boolean;
  onClick?: VoidFunction;
  onMouseEnter?: any;
  onMouseLeave?: any;
  disableHover?: boolean;
}>`
  ${({ theme, isLast, disableHover }) => css`
    color: ${isLast ? theme.colors.OnyxGrayD10 : theme.colors.OnyxGrayL10};
    margin-bottom: 2px;
    line-height: ${theme.spaces.s};
    cursor: default;

    ${!disableHover &&
    css`
      &:hover {
        color: ${theme.colors.OnyxGrayD20};
        cursor: pointer;
      }
    `}
  `}
`;

interface RenderNodeProps {
  nodeId: NodeId;
  isLast: boolean;
}

const nodeDomSelector = nodesFlux.selectors.getNodeDom(ROOT_NODE);

const RenderNode = ({ nodeId, isLast }: RenderNodeProps) => {
  const { actions, query } = useEditor();
  const rootDOM = useEditorSelector(nodeDomSelector);
  const type = query.node(nodeId)?.displayName();
  const componentSchema = useComponentSchema(type);

  const onClick = () => {
    // we need to focus the root dom for the useCheckConditions in events
    // to work properly for arrow keys navigation and other shortcuts
    rootDOM?.focus();
    actions.selectNode([nodeId]);
  };

  const onMouseEnter = () =>
    actions.setEventNodes(EventTypes.hovered, [nodeId], HISTORY_ACTION_MODE.IGNORE);
  const onMouseLeave = () =>
    actions.setEventNodes(EventTypes.hovered, [], HISTORY_ACTION_MODE.IGNORE);

  return (
    <>
      <BreadcrumbName
        element="span"
        isLast={isLast}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {componentSchema?.name || type}
      </BreadcrumbName>
      {!isLast && <ArrowRightIcon color="OnyxGrayL10" size="s" />}
    </>
  );
};

const BreadCrumbsContainer = styled.div`
  ${({ theme }) => css`
    padding: 0 ${theme.spaces.xs};
    display: flex;
    align-items: center;
  `}
`;

const multistepFormSelectionBlacklist = selectionBlacklist.filter(
  (n) => n !== BuiltInComponents.FormStep,
);

export const LayerSelection = () => {
  const { selection } = useSelection();
  const { ancestors } = useTraversal();
  const { isMultistepForm } = useFormAncestor(selection.all[0]);

  if (selection.isEmpty || selection.all.length > 1) {
    return null;
  }
  const { last } = selection;
  const breadcrumbs = [
    ...(ancestors(
      last,
      isMultistepForm ? { blacklistNodeTypes: multistepFormSelectionBlacklist } : undefined,
    )?.reverse() || []),
    last,
  ];
  return (
    <BreadCrumbsContainer>
      {breadcrumbs.map((nodeId, index) => (
        <RenderNode key={nodeId} nodeId={nodeId} isLast={index === breadcrumbs.length - 1} />
      ))}
    </BreadCrumbsContainer>
  );
};
